<template>
  <div>
    <!-- Session About to Start Countdown -->
    <div v-if="isSessionAboutToStart && !shouldStartVideo" class="tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-orange-50 tw-to-orange-100">
      <div class="tw-text-center tw-p-8">
        <div class="tw-w-24 tw-h-24 tw-bg-orange-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6">
          <icon icon="solar:play-circle-bold" class="tw-text-4xl tw-text-orange-600" />
        </div>
        <h2 class="tw-text-2xl tw-font-bold tw-text-orange-800 tw-mb-3">Session Starting Soon!</h2>
        <p class="tw-text-orange-600 tw-mb-4 tw-max-w-md">
          Get ready! This session will begin in just a few minutes.
        </p>
        
        <!-- Live Countdown -->
        <div class="tw-mt-6 tw-p-6 tw-bg-orange-100 tw-rounded-lg tw-border tw-border-orange-200">
          <div class="tw-text-sm tw-text-orange-700 tw-mb-2">Session starts in:</div>
          <div class="tw-text-4xl tw-font-bold tw-text-orange-800 tw-mb-2">
            {{ sessionStartCountdown ? formatCountdown(sessionStartCountdown) : timeUntilStart }}
          </div>
          <div class="tw-text-xs tw-text-orange-600">Preparing to go live...</div>
        </div>
      </div>
    </div>

    <!-- Session Not Started State -->
    <div v-else-if="isSessionNotStarted && !isSessionAboutToStart" 
    :style="{ backgroundImage: `url(${session?.image})` }"
    class="tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-bg-white tw-bg-cover tw-bg-center">
      <div class="tw-text-center tw-p-8 tw-bg-white tw-rounded">
        <div class="tw-w-24 tw-h-24 tw-bg-primary-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6">
          <icon icon="solar:clock-circle-bold" class="tw-text-4xl tw-text-primary-600" />
        </div>
        <h2 class="tw-text-2xl tw-font-bold tw-text-primary-800 tw-mb-3">Session Not Started</h2>
        <p class="tw-text-primary-600 tw-mb-4 tw-max-w-md">
          This session hasn't begun yet. Please wait for the scheduled start time.
        </p>
        <div class="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-text-sm tw-text-primary-500">
          <span class="tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:calendar-bold" class="tw-text-primary-400" />
            {{ formattedSessionDate }}
          </span>
          <span class="tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:clock-circle-bold" class="tw-text-primary-400" />
            {{ formattedDuration }}
          </span>
        </div>
        <!-- Countdown to start -->
        <div v-if="session?.start_date" class="tw-mt-6 tw-p-4 tw-bg-primary-100 tw-rounded-lg tw-border tw-border-primary-200">
          <div class="tw-text-sm tw-text-primary-700 tw-mb-2">Session starts in:</div>
          <div class="tw-text-2xl tw-font-bold tw-text-primary-800">{{ timeUntilStart }}</div>
        </div>
      </div>
    </div>

    <!-- Session Ended State -->
    <div v-else-if="isSessionEnded" class="tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-gray-100 tw-to-gray-200">
      <div class="tw-text-center tw-p-8">
        <div class="tw-w-24 tw-h-24 tw-bg-gray-300 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6">
          <icon icon="solar:check-circle-bold" class="tw-text-4xl tw-text-gray-600" />
        </div>
        <h2 class="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-3">Session Ended</h2>
        <p class="tw-text-gray-600 tw-mb-4 tw-max-w-md">
          This session has concluded. Thank you for participating!
        </p>
        <div class="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-text-sm tw-text-gray-500">
          <span class="tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:calendar-bold" class="tw-text-gray-400" />
            {{ formattedSessionDate }}
          </span>
          <span class="tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:clock-circle-bold" class="tw-text-gray-400" />
            {{ formattedDuration }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import moment from 'moment'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  isSessionNotStarted: {
    type: Boolean,
    default: false
  },
  isSessionAboutToStart: {
    type: Boolean,
    default: false
  },
  isSessionEnded: {
    type: Boolean,
    default: false
  },
  shouldStartVideo: {
    type: Boolean,
    default: false
  },
  sessionStartCountdown: {
    type: Number,
    default: null
  },
  timeUntilStart: {
    type: String,
    default: 'Time not specified'
  }
})

// Computed properties for formatting
const formattedSessionDate = computed(() => {
  if (!props.session?.start_date) return 'Date not specified'
  
  try {
    const dateObj = moment(props.session.start_date)
    const today = moment()
    const tomorrow = moment().add(1, 'day')

    if (dateObj.isSame(today, 'day')) {
      return `Today ${dateObj.format('h:mm A')}`
    } else if (dateObj.isSame(tomorrow, 'day')) {
      return `Tomorrow ${dateObj.format('h:mm A')}`
    } else {
      return dateObj.format('MMM D, h:mm A')
    }
  } catch (error) {
    console.error('Error formatting session date:', error)
    return 'Date not specified'
  }
})

const formattedDuration = computed(() => {
  if (!props.session?.start_date || !props.session?.end_date) return 'Duration not specified'
  
  try {
    const start = moment(props.session.start_date)
    const end = moment(props.session.end_date)
    const duration = moment.duration(end.diff(start))
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  } catch (error) {
    console.error('Error calculating duration:', error)
    return 'Duration not specified'
  }
})

// Format countdown timer for display
const formatCountdown = (seconds) => {
  if (!seconds || seconds <= 0) return 'Starting now...'
  
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  
  if (minutes > 0) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  
  return `${remainingSeconds}s`
}
</script>
